import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Bluetooth, Edit, Info, Plus, Power, RotateCw, Save, ShieldAlert, X } from "lucide-react";
import OperatorCardActions from "../components/OperatorCardActions";
import FilterPopover from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";
import SensorBleSetupModal from "../components/SensorBleSetupModal";
import { fetchLocations } from "../services/locations";
import {
    assignOperatorSensor,
    fetchOperatorSensorEvents,
    listOperatorSensors,
    updateOperatorSensor,
} from "../services/sensors";
import {
    DEFAULT_SENSOR_THRESHOLDS,
    DEFAULT_SENSOR_DEVICE_SETTINGS,
    deviceSettingsFromSensorConfig,
    mergeDeviceSettingsIntoConfig,
    mergeThresholdsIntoConfig,
    mergeTimezoneIntoConfig,
    resolveSensorChannel,
    thresholdsFromSensorConfig,
} from "../utils/sensorThresholds";
import {
    DEFAULT_SENSOR_TIMEZONE,
    SENSOR_TIMEZONE_OPTIONS,
    formatSensorDateTime,
    timezoneFromSensorConfig,
} from "../utils/sensorDateTime";
import "../styles/sensors.css";

const emptyAssignForm = {
    device_uid: "",
    pairing_code: "",
    location_id: "",
    display_name: "",
    descriptions: "",
};

const emptyEditForm = {
    display_name: "",
    descriptions: "",
    install_notes: "",
    notify_client: true,
    notify_operator: true,
    is_active: true,
    thresholds: { ...DEFAULT_SENSOR_THRESHOLDS },
    timezone: DEFAULT_SENSOR_TIMEZONE,
};

const severityLabelMap = {
    critical: "Критично",
    warning: "Попередження",
    info: "Інфо",
};

const statusLabelMap = {
    critical: "Тривога",
    warning: "Попередження",
    info: "Норма",
    resolved: "Нормалізовано",
};

const defaultAccess = { page_enabled: false, can_manage_network: false };

const formatDateTime = (value, timeZone = DEFAULT_SENSOR_TIMEZONE) => formatSensorDateTime(value, timeZone);

const formatWifiSignal = (rssi) => {
    const n = Number(rssi);
    if (!Number.isFinite(n)) return "";
    let quality = "слабкий";
    if (n >= -55) quality = "відмінний";
    else if (n >= -67) quality = "добрий";
    else if (n >= -75) quality = "середній";
    return `${n} дБм, ${quality}`;
};

const formatClimateMetricsLine = (sensor) => {
    const parts = [];
    if (sensor?.last_temp_c != null && Number.isFinite(Number(sensor.last_temp_c))) {
        parts.push(`${Number(sensor.last_temp_c).toFixed(1)} °C`);
    }
    if (sensor?.last_humidity != null && Number.isFinite(Number(sensor.last_humidity))) {
        parts.push(`${Math.round(Number(sensor.last_humidity))} %RH`);
    }
    if (sensor?.last_eco2 != null && Number.isFinite(Number(sensor.last_eco2))) {
        parts.push(`eCO₂ ${Math.round(Number(sensor.last_eco2))}`);
    }
    if (sensor?.last_tvoc != null && Number.isFinite(Number(sensor.last_tvoc))) {
        parts.push(`TVOC ${Math.round(Number(sensor.last_tvoc))}`);
    }
    if (sensor?.last_aqi != null && Number.isFinite(Number(sensor.last_aqi))) {
        parts.push(`AQI ${Math.round(Number(sensor.last_aqi))}`);
    }
    return parts.join(" · ");
};

const formatBatteryLine = (sensor) => {
    const level = Number(sensor?.last_battery_level);
    const volts = Number(sensor?.last_battery_volts);
    const hasLevel = Number.isFinite(level) && level >= 0 && level <= 100;
    const hasVolts = Number.isFinite(volts) && volts > 0.1;
    if (hasLevel) {
        return hasVolts ? `Батарея ${Math.round(level)}% (${volts.toFixed(2)} В)` : `Батарея ${Math.round(level)}%`;
    }
    if (hasVolts) {
        return `Батарея ${volts.toFixed(2)} В`;
    }
    return "";
};

const buildLocationLabel = (location) => {
    if (!location) return "";
    return [location.street, location.house_number, location.location_name].filter(Boolean).join(" ");
};

const OperatorSensors = ({ access = defaultAccess }) => {
    const [sensors, setSensors] = useState([]);
    const [filteredSensors, setFilteredSensors] = useState([]);
    const [locations, setLocations] = useState([]);
    const [events, setEvents] = useState([]);
    const [selectedSensorId, setSelectedSensorId] = useState(null);
    const [highlightedEventId, setHighlightedEventId] = useState(null);
    const [editingSensorId, setEditingSensorId] = useState(null);
    const [editedData, setEditedData] = useState(emptyEditForm);
    const [originalData, setOriginalData] = useState(emptyEditForm);
    const [assignForm, setAssignForm] = useState(emptyAssignForm);
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [locationFilter, setLocationFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [historyLocationFilter, setHistoryLocationFilter] = useState("");
    const [historySensorTypeFilter, setHistorySensorTypeFilter] = useState("");
    const [sortBy, setSortBy] = useState("display_name");
    const [sortDir, setSortDir] = useState("asc");
    const [eventDateFrom, setEventDateFrom] = useState("");
    const [eventDateTo, setEventDateTo] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showSensorsHint, setShowSensorsHint] = useState(false);
    const [expandedSensorId, setExpandedSensorId] = useState(null);
    const [bleSetupSensor, setBleSetupSensor] = useState(null);

    const selectedSensor = useMemo(
        () => sensors.find((item) => Number(item.id) === Number(selectedSensorId)) || null,
        [sensors, selectedSensorId]
    );
    const historySensorTypes = useMemo(() => {
        const types = new Set();
        sensors.forEach((item) => {
            if (item.sensor_type) types.add(item.sensor_type);
        });
        return Array.from(types).sort();
    }, [sensors]);
    const historyFilterSensors = useMemo(() => sensors.filter((sensor) => {
        if (historyLocationFilter && String(sensor.location_id) !== String(historyLocationFilter)) {
            return false;
        }
        if (historySensorTypeFilter && sensor.sensor_type !== historySensorTypeFilter) {
            return false;
        }
        return true;
    }), [sensors, historyLocationFilter, historySensorTypeFilter]);

    const loadData = useCallback(async () => {
        if (!access.page_enabled) {
            setSensors([]);
            setLocations([]);
            return [];
        }

        const [sensorData, locationData] = await Promise.all([
            listOperatorSensors(),
            fetchLocations().catch(() => []),
        ]);
        const normalizedSensors = Array.isArray(sensorData) ? sensorData : [];
        setSensors(normalizedSensors);
        setLocations(Array.isArray(locationData) ? locationData : []);
        return normalizedSensors;
    }, [access.page_enabled]);

    const loadEvents = useCallback(async (sensorId, dateFrom = "", dateTo = "") => {
        if (!sensorId) {
            setEvents([]);
            return;
        }
        const response = await fetchOperatorSensorEvents(sensorId, {
            limit: 100,
            dateFrom,
            dateTo,
        });
        setEvents(Array.isArray(response?.items) ? response.items : []);
    }, []);

    const refreshAll = useCallback(async () => {
        if (!access.page_enabled) {
            setSensors([]);
            setEvents([]);
            setSelectedSensorId(null);
            return;
        }

        try {
            setLoading(true);
            setMessage("");
            const sensorData = await loadData();
            setSelectedSensorId((current) => {
                if (current && sensorData.some((item) => Number(item.id) === Number(current))) {
                    return current;
                }
                return sensorData[0]?.id || null;
            });
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [access.page_enabled, loadData]);

    useEffect(() => {
        refreshAll();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [refreshAll]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!access.page_enabled || !selectedSensorId) {
                setEvents([]);
                return;
            }
            try {
                const response = await fetchOperatorSensorEvents(selectedSensorId, {
                    limit: 100,
                    dateFrom: eventDateFrom,
                    dateTo: eventDateTo,
                });
                if (!cancelled) {
                    setEvents(Array.isArray(response?.items) ? response.items : []);
                }
            } catch (error) {
                if (!cancelled) {
                    setMessage(`❌ ${error.message}`);
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [access.page_enabled, selectedSensorId, eventDateFrom, eventDateTo]);

    useEffect(() => {
        if (!sensors.length) return;

        let pendingSensorId = null;
        let pendingEventId = null;
        try {
            pendingSensorId = parseInt(localStorage.getItem("pendingSensorOpenId_operator"), 10);
            pendingEventId = parseInt(localStorage.getItem("pendingSensorEventOpenId_operator"), 10);
        } catch (_) {
            pendingSensorId = null;
            pendingEventId = null;
        }

        if (Number.isFinite(pendingSensorId) && sensors.some((item) => Number(item.id) === pendingSensorId)) {
            setSelectedSensorId(pendingSensorId);
        }
        if (Number.isFinite(pendingEventId) && pendingEventId > 0) {
            setHighlightedEventId(pendingEventId);
        }

        try {
            localStorage.removeItem("pendingSensorOpenId_operator");
            localStorage.removeItem("pendingSensorEventOpenId_operator");
        } catch (_) {
            // ignore
        }
    }, [sensors]);

    useEffect(() => {
        if (!historyFilterSensors.length) {
            setSelectedSensorId(null);
            return;
        }
        if (!selectedSensorId || !historyFilterSensors.some((item) => Number(item.id) === Number(selectedSensorId))) {
            setSelectedSensorId(historyFilterSensors[0].id);
        }
    }, [historyFilterSensors, selectedSensorId]);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = sensors.filter((sensor) => {
            const matchesSearch =
                (sensor.device_uid || "").toString().toLowerCase().includes(q) ||
                (sensor.display_name || "").toString().toLowerCase().includes(q) ||
                (sensor.descriptions || "").toString().toLowerCase().includes(q) ||
                (sensor.sensor_type || "").toString().toLowerCase().includes(q) ||
                (sensor.location_name || "").toString().toLowerCase().includes(q);

            const matchesLocation =
                !locationFilter || String(sensor.location_id) === String(locationFilter);

            const matchesStatus =
                statusFilter === "all" ||
                (statusFilter === "active" && sensor.is_active === true) ||
                (statusFilter === "inactive" && sensor.is_active !== true);

            return matchesSearch && matchesLocation && matchesStatus;
        });
        setFilteredSensors(filtered);
    }, [sensors, searchQuery, locationFilter, statusFilter]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        const sorted = [...filteredSensors];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            if (typeof valA === "boolean") {
                valA = valA ? 1 : 0;
                valB = valB ? 1 : 0;
            } else {
                valA = String(valA).toLowerCase();
                valB = String(valB).toLowerCase();
            }
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            }
            return valA < valB ? 1 : valA > valB ? -1 : 0;
        });
        return sorted;
    };

    const startEdit = (sensor) => {
        setEditingSensorId(sensor.id);
        setExpandedSensorId(sensor.id);
        const nextForm = {
            display_name: sensor.display_name || "",
            descriptions: sensor.descriptions || "",
            install_notes: sensor.install_notes || "",
            notify_client: Boolean(sensor.notify_client),
            notify_operator: Boolean(sensor.notify_operator),
            is_active: Boolean(sensor.is_active),
            thresholds: thresholdsFromSensorConfig(sensor.config),
            timezone: timezoneFromSensorConfig(sensor.config),
            wifi_ssid_alt: String(sensor?.config?.wifi_ssid_alt || ""),
            wifi_ssid: String(sensor?.config?.wifi_ssid || ""),
            wifi_primary_ssid: String(sensor?.config?.wifi_primary_ssid || sensor?.config?.wifi_ssid || ""),
            wifi_pass_alt: "",
            wifi_alt_clear: false,
            ...deviceSettingsFromSensorConfig(sensor.config),
        };
        setEditedData(nextForm);
        setOriginalData(nextForm);
    };

    const handleCancelEdit = () => {
        setEditedData(originalData);
        setEditingSensorId(null);
    };

    const handleBuzzerToggle = async (sensor, enabled) => {
        const id = Number(sensor?.id);
        if (!id || sensor?.is_private) return;
        try {
            setSaving(true);
            setMessage("");
            const nextConfig = mergeDeviceSettingsIntoConfig(
                sensor.config,
                { buzzer_enabled: Boolean(enabled) },
                sensor
            );
            await updateOperatorSensor(id, { config: nextConfig });
            await refreshAll();
            setMessage(
                enabled
                    ? "✅ Біпер увімкнено. Чип підхопить на наступній події/heartbeat або через BLE."
                    : "✅ Біпер вимкнено. Чип підхопить на наступній події/heartbeat або через BLE."
            );
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const toggleSensorExpanded = (sensorId, isEditing) => {
        if (isEditing) return;
        setExpandedSensorId((prev) => (prev === sensorId ? null : sensorId));
        setSelectedSensorId(sensorId);
    };

    const handleAssign = async () => {
        try {
            setSaving(true);
            setMessage("");
            await assignOperatorSensor({
                device_uid: assignForm.device_uid.trim().toUpperCase(),
                pairing_code: String(assignForm.pairing_code || "").trim().toUpperCase(),
                location_id: assignForm.location_id ? Number(assignForm.location_id) : null,
                display_name: assignForm.display_name,
                descriptions: assignForm.descriptions,
            });
            setAssignForm(emptyAssignForm);
            setShowAddModal(false);
            await refreshAll();
            setMessage("✅ Публічний сенсор додано до локації");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async (sensorId) => {
        try {
            setSaving(true);
            setMessage("");
            const currentSensor = sensors.find((item) => Number(item.id) === Number(sensorId));
            const channel = resolveSensorChannel(currentSensor || {});
            await updateOperatorSensor(sensorId, {
                display_name: editedData.display_name,
                descriptions: editedData.descriptions,
                install_notes: editedData.install_notes,
                notify_client: Boolean(editedData.notify_client),
                notify_operator: Boolean(editedData.notify_operator),
                is_active: Boolean(editedData.is_active),
                config: {
                    ...mergeTimezoneIntoConfig(
                        mergeDeviceSettingsIntoConfig(
                            mergeThresholdsIntoConfig(
                                currentSensor?.config,
                                editedData.thresholds,
                                channel
                            ),
                            {
                                heartbeat_interval_sec: editedData.heartbeat_interval_sec,
                                buzzer_enabled: editedData.buzzer_enabled,
                            }
                        ),
                        editedData.timezone
                    ),
                    ...(currentSensor && !currentSensor.is_private ? {
                        wifi_ssid: String(editedData.wifi_ssid || currentSensor?.config?.wifi_ssid || "").trim(),
                        wifi_ssid_alt: String(editedData.wifi_ssid_alt || "").trim(),
                        wifi_pass_alt: String(editedData.wifi_pass_alt || ""),
                        wifi_alt_clear: Boolean(editedData.wifi_alt_clear) || !String(editedData.wifi_ssid_alt || "").trim(),
                        wifi_primary_ssid: String(editedData.wifi_primary_ssid || editedData.wifi_ssid || "").trim(),
                        wifi_connect_ssid: String(editedData.wifi_primary_ssid || editedData.wifi_ssid || "").trim(),
                    } : {}),
                },
            });
            await refreshAll();
            await loadEvents(sensorId, eventDateFrom, eventDateTo);
            setEditingSensorId(null);
            setMessage("✅ Сенсор оновлено. Біпер на чипі підхопиться на наступній події/heartbeat або через BLE.");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleRebootSensor = async (sensor) => {
        if (!sensor || sensor.is_private) return;
        try {
            setSaving(true);
            setMessage("");
            const connectSsid = String(
                editedData.wifi_primary_ssid
                || sensor?.config?.wifi_primary_ssid
                || sensor?.config?.wifi_ssid
                || ""
            ).trim();
            await updateOperatorSensor(sensor.id, {
                config: {
                    ...(sensor.config && typeof sensor.config === "object" ? sensor.config : {}),
                    reboot_requested: true,
                    wifi_connect_ssid: connectSsid,
                    wifi_primary_ssid: connectSsid,
                },
            });
            setMessage("✅ Команду перезавантаження надіслано. Сенсор виконає її на наступному виході на зв'язок.");
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleBleSetupComplete = async ({ notificationMode }) => {
        if (!bleSetupSensor?.id) return;
        try {
            const currentSensor = sensors.find((item) => Number(item.id) === Number(bleSetupSensor.id));
            if (!currentSensor || currentSensor.is_private) return;

            const nextConfig = {
                ...(currentSensor.config && typeof currentSensor.config === "object" ? currentSensor.config : {}),
                ble_provisioned_at: new Date().toISOString(),
                notification_mode: notificationMode,
            };

            await updateOperatorSensor(bleSetupSensor.id, { config: nextConfig });
            await refreshAll();
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setBleSetupSensor(null);
        }
    };

    const canBleSetupSensor = (sensor) => Boolean(
        access.can_manage_network &&
        !sensor?.is_private &&
        sensor?.location_id
    );

    const renderBleSetupButton = (sensor) => {
        if (!canBleSetupSensor(sensor)) return null;
        return (
            <button
                type="button"
                className="add-btn operator-sensors-ble-btn"
                onClick={(event) => {
                    event.stopPropagation();
                    setBleSetupSensor(sensor);
                }}
                disabled={saving}
            >
                <Bluetooth size={16} /> Налаштувати через Bluetooth
            </button>
        );
    };

    const renderFlagEditor = (form, setter, sensor = null) => (
        <div className="operator-sensors-flags">
            <label className="operator-sensors-check-inline">
                <input
                    type="checkbox"
                    checked={form.notify_client === true}
                    onChange={(e) => setter((prev) => ({ ...prev, notify_client: e.target.checked }))}
                />
                <span>Сповіщення клієнту</span>
            </label>
            <label className="operator-sensors-check-inline">
                <input
                    type="checkbox"
                    checked={form.notify_operator === true}
                    onChange={(e) => setter((prev) => ({ ...prev, notify_operator: e.target.checked }))}
                />
                <span>Сповіщення оператору</span>
            </label>
            {!sensor?.is_private ? (
                <div className="client-sensors-notify-radios" role="radiogroup" aria-label="Біпер сенсора">
                    <label className="operator-sensors-check-inline">
                        <input
                            type="radio"
                            name={`buzzer-edit-${sensor?.id || "form"}`}
                            checked={form.buzzer_enabled !== false}
                            disabled={saving}
                            onChange={() => setter((prev) => ({ ...prev, buzzer_enabled: true }))}
                        />
                        <span>Біпер увімкнено</span>
                    </label>
                    <label className="operator-sensors-check-inline">
                        <input
                            type="radio"
                            name={`buzzer-edit-${sensor?.id || "form"}`}
                            checked={form.buzzer_enabled === false}
                            disabled={saving}
                            onChange={() => setter((prev) => ({ ...prev, buzzer_enabled: false }))}
                        />
                        <span>Біпер вимкнено</span>
                    </label>
                    <div className="client-sensors-mode-note">
                        Біпер публічного сенсора. Під час тривоги зміна підхопиться на наступній події/heartbeat (або через BLE).
                    </div>
                </div>
            ) : (
                <div className="client-sensors-mode-note">Біпер приватного сенсора керує лише власник-клієнт.</div>
            )}
            <label className="operator-sensors-check-inline">
                <input
                    type="checkbox"
                    checked={form.is_active === true}
                    onChange={(e) => setter((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
                <span>Активний</span>
            </label>
        </div>
    );

    const renderFlagPreview = (sensor) => {
        const settings = deviceSettingsFromSensorConfig(sensor.config, sensor);
        const buzzerOn = settings.buzzer_enabled !== false;
        return (
            <div className="operator-sensors-flags operator-sensors-flags-preview">
                {sensor.notify_client ? <span className="operator-sensors-flag-badge">Клієнт</span> : null}
                {sensor.notify_operator ? <span className="operator-sensors-flag-badge">Оператор</span> : null}
                {sensor.is_active ? <span className="operator-sensors-flag-badge is-active">Активний</span> : <span className="operator-sensors-flag-badge is-muted">Вимкнений</span>}
                {!sensor.is_private ? (
                    <div className="client-sensors-notify-radios" role="radiogroup" aria-label="Біпер сенсора">
                        <label className="operator-sensors-check-inline">
                            <input
                                type="radio"
                                name={`buzzer-preview-${sensor.id}`}
                                checked={buzzerOn}
                                disabled={saving}
                                onChange={() => handleBuzzerToggle(sensor, true)}
                            />
                            <span>Біпер увімкнено</span>
                        </label>
                        <label className="operator-sensors-check-inline">
                            <input
                                type="radio"
                                name={`buzzer-preview-${sensor.id}`}
                                checked={!buzzerOn}
                                disabled={saving}
                                onChange={() => handleBuzzerToggle(sensor, false)}
                            />
                            <span>Біпер вимкнено</span>
                        </label>
                    </div>
                ) : (
                    <div className="client-sensors-mode-note">
                        Біпер: {buzzerOn ? "увімкнено" : "вимкнено"} (керує власник)
                    </div>
                )}
            </div>
        );
    };

    const renderAddModal = () => {
        if (!showAddModal) return null;

        return (
            <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                <div className="modal-content operator-mobile-modal operator-form-modal operator-sensors-modal" onClick={(e) => e.stopPropagation()}>
                    <h3>Додати публічний сенсор</h3>
                    <div className="operator-form-grid">
                        <input
                            type="text"
                            placeholder="Серійний номер"
                            value={assignForm.device_uid}
                            onChange={(e) => setAssignForm({ ...assignForm, device_uid: e.target.value })}
                        />
                        <input
                            type="text"
                            placeholder="Код активації"
                            value={assignForm.pairing_code}
                            onChange={(e) => setAssignForm({ ...assignForm, pairing_code: e.target.value })}
                        />
                        <div className="operator-sensors-inline-note">Оператор активує лише публічні сенсори за серійним номером і кодом активації. Приватні сенсори клієнти додають самостійно.</div>
                        <select
                            value={assignForm.location_id}
                            onChange={(e) => setAssignForm({ ...assignForm, location_id: e.target.value })}
                        >
                            <option value="">Оберіть локацію</option>
                            {locations.map((location) => (
                                <option key={location.id} value={location.id}>
                                    {buildLocationLabel(location) || location.location_name || "Без назви"}
                                </option>
                            ))}
                        </select>
                        <input
                            className="operator-form-span-2"
                            type="text"
                            placeholder="Назва сенсора"
                            value={assignForm.display_name}
                            onChange={(e) => setAssignForm({ ...assignForm, display_name: e.target.value })}
                        />
                        <textarea
                            className="operator-form-span-2"
                            placeholder="Опис або примітка"
                            value={assignForm.descriptions}
                            onChange={(e) => setAssignForm({ ...assignForm, descriptions: e.target.value })}
                            rows={3}
                        />
                    </div>
                    <div className="modal-actions">
                        <button className="add-btn" onClick={handleAssign} disabled={saving}>
                            Додати
                        </button>
                        <button className="add-btn cancel-btn" onClick={() => setShowAddModal(false)} disabled={saving}>
                            Скасувати
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const filterActive =
        Boolean(locationFilter) ||
        statusFilter !== "all" ||
        Boolean(historyLocationFilter) ||
        Boolean(historySensorTypeFilter) ||
        Boolean(eventDateFrom) ||
        Boolean(eventDateTo);

    const clearAllFilters = () => {
        setLocationFilter("");
        setStatusFilter("all");
        setHistoryLocationFilter("");
        setHistorySensorTypeFilter("");
        setEventDateFrom("");
        setEventDateTo("");
    };

    if (!access.page_enabled) {
        return (
            <div className="operator-sensors-page">
                <section className="sensor-panel sensor-disabled-panel">
                    <div className="sensor-panel-header">
                        <h3><ShieldAlert size={18} /> Сенсори недоступні</h3>
                    </div>
                    <div className="sensor-empty-state">
                        Сенсори мережі вимкнені. Попросіть адміністратора додати або увімкнути сервіс мережі з функцією «Сенсори»
                        (`feature_key=sensors`) і призначити його цьому оператору. Приватні сенсори клієнти додають самостійно.
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="operator-sensors-page">
            <div className="config-header operator-sensors-toolbar">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} disabled={saving} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={refreshAll} disabled={loading || saving} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <FilterPopover
                        active={filterActive}
                        title="Фільтри сенсорів"
                        buttonTitle="Фільтри сенсорів"
                        onClear={clearAllFilters}
                    >
                        <div className="toolbar-filter-section">
                            <div className="toolbar-filter-section__title">Список</div>
                            <label className="toolbar-filter-field">
                                <span>Локація</span>
                                <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                                    <option value="">Усі локації</option>
                                    {locations.map((location) => (
                                        <option key={location.id} value={location.id}>
                                            {buildLocationLabel(location) || location.location_name || "Без назви"}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="toolbar-filter-field">
                                <span>Статус</span>
                                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="all">Усі</option>
                                    <option value="active">Активні</option>
                                    <option value="inactive">Неактивні</option>
                                </select>
                            </label>
                        </div>
                        <div className="toolbar-filter-section">
                            <div className="toolbar-filter-section__title">Історія</div>
                            <label className="toolbar-filter-field">
                                <span>Локація</span>
                                <select value={historyLocationFilter} onChange={(e) => setHistoryLocationFilter(e.target.value)}>
                                    <option value="">Усі локації</option>
                                    {locations.map((location) => (
                                        <option key={location.id} value={location.id}>
                                            {buildLocationLabel(location) || location.location_name || "Без назви"}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="toolbar-filter-field">
                                <span>Тип сенсора</span>
                                <select value={historySensorTypeFilter} onChange={(e) => setHistorySensorTypeFilter(e.target.value)}>
                                    <option value="">Усі типи сенсорів</option>
                                    {historySensorTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="toolbar-filter-field">
                                <span>Від</span>
                                <input
                                    id="operator-sensor-date-from"
                                    type="date"
                                    className="wr-date-input"
                                    value={eventDateFrom}
                                    onChange={(e) => setEventDateFrom(e.target.value)}
                                />
                            </label>
                            <label className="toolbar-filter-field">
                                <span>До</span>
                                <input
                                    id="operator-sensor-date-to"
                                    type="date"
                                    className="wr-date-input"
                                    value={eventDateTo}
                                    onChange={(e) => setEventDateTo(e.target.value)}
                                />
                            </label>
                        </div>
                    </FilterPopover>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук"
                    />
                </div>
            </div>

            {message ? <div className="message-banner">{message}</div> : null}
            <div className={`operator-sensors-hint ${showSensorsHint ? "is-open" : ""}`}>
                <button
                    type="button"
                    className="operator-sensors-hint__toggle"
                    onClick={() => setShowSensorsHint((v) => !v)}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    aria-expanded={showSensorsHint}
                    title="Підказка"
                    aria-label="Підказка щодо Bluetooth"
                >
                    <Info size={16} />
                    <span>{showSensorsHint ? "Сховати підказку" : "Підказка Bluetooth"}</span>
                </button>
                {showSensorsHint ? (
                    <div className="operator-sensors-hint__body">
                        Після додавання публічного сенсора на локацію налаштуйте Wi-Fi і канали сповіщень через Bluetooth поруч із пристроєм.
                    </div>
                ) : null}
            </div>
            {renderAddModal()}
            {bleSetupSensor ? (
                <SensorBleSetupModal
                    sensor={bleSetupSensor}
                    onClose={() => setBleSetupSensor(null)}
                    onComplete={handleBleSetupComplete}
                />
            ) : null}

            {isMobile ? (
                <div className="operator-accordion-list">
                    {getSortedData().map((sensor) => {
                        const isEditing = editingSensorId === sensor.id;
                        const isExpanded = expandedSensorId === sensor.id || isEditing;
                        const statusLabel = statusLabelMap[String(sensor.last_status || "info").toLowerCase()] || sensor.last_status || "Норма";

                        return (
                            <div
                                key={sensor.id}
                                className={`operator-accordion-item config-card operator-sensors-card ${Number(selectedSensorId) === Number(sensor.id) ? "is-selected" : ""}`}
                            >
                                <button
                                    type="button"
                                    className="operator-accordion-head"
                                    onClick={() => toggleSensorExpanded(sensor.id, isEditing)}
                                    aria-expanded={isExpanded}
                                >
                                    <div className="operator-accordion-head-main">
                                        <div className="operator-accordion-title">{sensor.display_name || sensor.device_uid}</div>
                                        <div className="operator-accordion-meta">
                                            <span>{statusLabel}</span>
                                            <span>{sensor.sensor_type || "-"}</span>
                                            <span>{sensor.is_private ? "Приватний" : "Публічний"}</span>
                                            {formatClimateMetricsLine(sensor) ? <span>{formatClimateMetricsLine(sensor)}</span> : null}
                                            {formatBatteryLine(sensor) ? <span>{formatBatteryLine(sensor)}</span> : null}
                                        </div>
                                    </div>
                                    <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                                </button>

                                <OperatorCardActions
                                    editing={isEditing}
                                    onEdit={() => startEdit(sensor)}
                                    onSave={() => handleSave(sensor.id)}
                                    onCancel={handleCancelEdit}
                                    disabled={saving}
                                    stopPropagation
                                />
                                {renderBleSetupButton(sensor)}

                                {isExpanded ? (
                                    <div className="operator-accordion-body">
                                <div><strong>Серійний номер:</strong> <span>{sensor.device_uid}</span></div>
                                <div><strong>Назва:</strong> {isEditing ? (
                                    <input value={editedData.display_name} onChange={(e) => setEditedData({ ...editedData, display_name: e.target.value })} />
                                ) : <span>{sensor.display_name}</span>}</div>
                                <div><strong>Тип:</strong> <span>{sensor.sensor_type || "-"}</span></div>
                                <div><strong>Режим:</strong> <span>{sensor.is_private ? "Приватний" : "Публічний"}</span></div>
                                <div><strong>Клієнт:</strong> <span>{sensor.client_name || "-"}</span></div>
                                <div><strong>Локація:</strong> <span>{sensor.location_name || "-"}</span></div>
                                <div><strong>Останній зв'язок:</strong> <span>{formatDateTime(sensor.last_seen_at, timezoneFromSensorConfig(sensor.config))}</span></div>
                                {sensor.last_signal_rssi != null ? (
                                    <div><strong>Сигнал Wi-Fi:</strong> <span>{formatWifiSignal(sensor.last_signal_rssi) || sensor.last_signal_rssi}</span></div>
                                ) : null}
                                {formatClimateMetricsLine(sensor) ? (
                                    <div><strong>Клімат / повітря:</strong> <span>{formatClimateMetricsLine(sensor)}</span></div>
                                ) : null}
                                {formatBatteryLine(sensor) ? (
                                    <div><strong>Батарея:</strong> <span>{formatBatteryLine(sensor)}</span></div>
                                ) : null}
                                <div><strong>Статус:</strong> <span>{statusLabel}</span></div>
                                <div><strong>Опис:</strong> {isEditing ? (
                                    <textarea value={editedData.descriptions} onChange={(e) => setEditedData({ ...editedData, descriptions: e.target.value })} rows={3} />
                                ) : <span>{sensor.descriptions || "-"}</span>}</div>
                                <div><strong>Монтаж:</strong> {isEditing ? (
                                    <textarea value={editedData.install_notes} onChange={(e) => setEditedData({ ...editedData, install_notes: e.target.value })} rows={3} />
                                ) : <span>{sensor.install_notes || "-"}</span>}</div>
                                <div><strong>Чутливість:</strong> {isEditing ? (
                                    <div className="client-sensors-form-grid">
                                        <label>Часовий пояс
                                            <select value={editedData.timezone || DEFAULT_SENSOR_TIMEZONE} onChange={(e) => setEditedData((prev) => ({ ...prev, timezone: e.target.value }))}>
                                                {SENSOR_TIMEZONE_OPTIONS.map((item) => (
                                                    <option key={item.value} value={item.value}>{item.label}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label>Heartbeat (сек)
                                            <input type="number" min={15} max={86400} step={15} value={editedData.heartbeat_interval_sec ?? DEFAULT_SENSOR_DEVICE_SETTINGS.heartbeat_interval_sec} onChange={(e) => setEditedData((prev) => ({ ...prev, heartbeat_interval_sec: e.target.value }))} />
                                        </label>
                                        {!sensor.is_private ? (
                                            <>
                                                <label>Альтернативний Wi-Fi SSID
                                                    <input
                                                        type="text"
                                                        value={editedData.wifi_ssid_alt || ""}
                                                        placeholder="Опційно / порожньо = очистити"
                                                        onChange={(e) => setEditedData((prev) => ({
                                                            ...prev,
                                                            wifi_ssid_alt: e.target.value,
                                                            wifi_alt_clear: !String(e.target.value || "").trim(),
                                                        }))}
                                                    />
                                                </label>
                                                <label>Пароль альтернативного Wi-Fi
                                                    <input
                                                        type="password"
                                                        value={editedData.wifi_pass_alt || ""}
                                                        placeholder="Новий пароль або залиште порожнім"
                                                        disabled={!String(editedData.wifi_ssid_alt || "").trim()}
                                                        onChange={(e) => setEditedData((prev) => ({ ...prev, wifi_pass_alt: e.target.value }))}
                                                    />
                                                </label>
                                            </>
                                        ) : null}
                                        {!sensor.is_private ? (
                                            <>
                                                {["wifi_ssid", "wifi_ssid_alt"].map((key) => {
                                                    const ssid = String(
                                                        key === "wifi_ssid"
                                                            ? (editedData.wifi_ssid || sensor?.config?.wifi_ssid || "")
                                                            : (editedData.wifi_ssid_alt || "")
                                                    ).trim();
                                                    if (!ssid) return null;
                                                    const selected = String(editedData.wifi_primary_ssid || editedData.wifi_ssid || sensor?.config?.wifi_ssid || "").trim();
                                                    return (
                                                        <label key={`${sensor.id}-${key}-${ssid}`} className="client-sensors-check-inline">
                                                            <input
                                                                type="radio"
                                                                name={`wifi-primary-op-${sensor.id}`}
                                                                checked={selected === ssid}
                                                                onChange={() => setEditedData((prev) => ({ ...prev, wifi_primary_ssid: ssid }))}
                                                            />
                                                            <span>{ssid}{key === "wifi_ssid" ? " · головна" : " · запасна"}</span>
                                                        </label>
                                                    );
                                                })}
                                                <button
                                                    type="button"
                                                    className="add-btn add-btn--secondary"
                                                    onClick={() => handleRebootSensor(sensor)}
                                                    disabled={saving}
                                                >
                                                    <Power size={14} /> Перезавантажити сенсор
                                                </button>
                                            </>
                                        ) : null}
                                        {(resolveSensorChannel(sensor) === "mq2" || resolveSensorChannel(sensor) === "both") ? (
                                            <>
                                                <label>Дим попередження<input type="number" min={0} max={4095} value={editedData.thresholds?.mq2_warning ?? DEFAULT_SENSOR_THRESHOLDS.mq2_warning} onChange={(e) => setEditedData((prev) => ({ ...prev, thresholds: { ...prev.thresholds, mq2_warning: e.target.value } }))} /></label>
                                                <label>Дим тривога<input type="number" min={0} max={4095} value={editedData.thresholds?.mq2_alarm ?? DEFAULT_SENSOR_THRESHOLDS.mq2_alarm} onChange={(e) => setEditedData((prev) => ({ ...prev, thresholds: { ...prev.thresholds, mq2_alarm: e.target.value } }))} /></label>
                                            </>
                                        ) : null}
                                        {(resolveSensorChannel(sensor) === "mq135" || resolveSensorChannel(sensor) === "both") ? (
                                            <>
                                                <label>Повітря попередження<input type="number" min={0} max={4095} value={editedData.thresholds?.mq135_warning ?? DEFAULT_SENSOR_THRESHOLDS.mq135_warning} onChange={(e) => setEditedData((prev) => ({ ...prev, thresholds: { ...prev.thresholds, mq135_warning: e.target.value } }))} /></label>
                                                <label>Повітря тривога<input type="number" min={0} max={4095} value={editedData.thresholds?.mq135_alarm ?? DEFAULT_SENSOR_THRESHOLDS.mq135_alarm} onChange={(e) => setEditedData((prev) => ({ ...prev, thresholds: { ...prev.thresholds, mq135_alarm: e.target.value } }))} /></label>
                                            </>
                                        ) : null}
                                    </div>
                                ) : (
                                    <span>
                                        {timezoneFromSensorConfig(sensor.config)};
                                        {(() => {
                                            const ch = resolveSensorChannel(sensor);
                                            const th = thresholdsFromSensorConfig(sensor.config);
                                            const settings = deviceSettingsFromSensorConfig(sensor.config);
                                            const hb = ` heartbeat ${settings.heartbeat_interval_sec}с`;
                                            const buzz = settings.buzzer_enabled !== false ? ", звук on" : ", звук off";
                                            const wifiPrimary = sensor?.config?.wifi_ssid
                                                ? `, wifi ${sensor.config.wifi_ssid}`
                                                : "";
                                            const wifiAlt = sensor?.config?.wifi_ssid_alt
                                                ? `, wifi2 ${sensor.config.wifi_ssid_alt}`
                                                : "";
                                            const wifiSignal = formatWifiSignal(sensor.last_signal_rssi);
                                            const rssi = wifiSignal ? `, ${wifiSignal}` : "";
                                            if (ch === "mq135") return ` повітря ${th.mq135_warning}/${th.mq135_alarm};${hb}${buzz}${wifiPrimary}${wifiAlt}${rssi}`;
                                            if (ch === "mq2") return ` дим ${th.mq2_warning}/${th.mq2_alarm};${hb}${buzz}${wifiPrimary}${wifiAlt}${rssi}`;
                                            if (ch === "ens160") {
                                                return ` eCO₂ ${th.eco2_warning}/${th.eco2_alarm}, TVOC ${th.tvoc_warning}/${th.tvoc_alarm};${hb}${buzz}${wifiPrimary}${wifiAlt}${rssi}`;
                                            }
                                            return ` дим ${th.mq2_warning}/${th.mq2_alarm}, повітря ${th.mq135_warning}/${th.mq135_alarm};${hb}${buzz}${wifiPrimary}${wifiAlt}${rssi}`;
                                        })()}
                                    </span>
                                )}</div>
                                <div>
                                    <strong>Прапорці:</strong>
                                    <div className="operator-sensors-card-flags">
                                        {isEditing ? renderFlagEditor(editedData, setEditedData, sensor) : renderFlagPreview(sensor)}
                                    </div>
                                </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="operator-sensors-table">
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("device_uid")}>Серійний номер {sortBy === "device_uid" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("display_name")}>Назва {sortBy === "display_name" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("sensor_type")}>Тип {sortBy === "sensor_type" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th>Режим</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("location_name")}>Локація {sortBy === "location_name" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th>Опис</th>
                                <th>Монтаж</th>
                                <th>Прапорці</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("last_seen_at")}>Останній зв'язок {sortBy === "last_seen_at" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th>Показання</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("last_status")}>Статус {sortBy === "last_status" && (sortDir === "asc" ? "▲" : "▼")}</th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((sensor) => {
                                const isEditing = editingSensorId === sensor.id;
                                return (
                                    <tr
                                        key={sensor.id}
                                        className={Number(selectedSensorId) === Number(sensor.id) ? "operator-sensors-row-selected" : ""}
                                        onClick={() => setSelectedSensorId(sensor.id)}
                                    >
                                        <td>{sensor.device_uid}</td>
                                        <td>
                                            {isEditing ? (
                                                <input value={editedData.display_name} onChange={(e) => setEditedData({ ...editedData, display_name: e.target.value })} />
                                            ) : sensor.display_name}
                                        </td>
                                        <td>{sensor.sensor_type || "-"}</td>
                                        <td>{sensor.is_private ? "Приватний" : "Публічний"}</td>
                                        <td>{sensor.location_name || "-"}</td>
                                        <td className="operator-sensors-cell-text">
                                            {isEditing ? (
                                                <textarea value={editedData.descriptions} onChange={(e) => setEditedData({ ...editedData, descriptions: e.target.value })} rows={3} />
                                            ) : <span>{sensor.descriptions || "-"}</span>}
                                        </td>
                                        <td className="operator-sensors-cell-text">
                                            {isEditing ? (
                                                <textarea value={editedData.install_notes} onChange={(e) => setEditedData({ ...editedData, install_notes: e.target.value })} rows={3} />
                                            ) : <span>{sensor.install_notes || "-"}</span>}
                                        </td>
                                        <td className="operator-sensors-cell-flags" onClick={(e) => e.stopPropagation()}>
                                            {isEditing ? (
                                                <>
                                                    {renderFlagEditor(editedData, setEditedData, sensor)}
                                                    <div className="client-sensors-form-grid" style={{ marginTop: 8 }}>
                                                        <label>Часовий пояс
                                                            <select value={editedData.timezone || DEFAULT_SENSOR_TIMEZONE} onChange={(e) => setEditedData((prev) => ({ ...prev, timezone: e.target.value }))}>
                                                                {SENSOR_TIMEZONE_OPTIONS.map((item) => (
                                                                    <option key={item.value} value={item.value}>{item.label}</option>
                                                                ))}
                                                            </select>
                                                        </label>
                                                        <label>Heartbeat (сек)
                                                            <input type="number" min={15} max={86400} step={15} value={editedData.heartbeat_interval_sec ?? DEFAULT_SENSOR_DEVICE_SETTINGS.heartbeat_interval_sec} onChange={(e) => setEditedData((prev) => ({ ...prev, heartbeat_interval_sec: e.target.value }))} />
                                                        </label>
                                                        {!sensor.is_private ? (
                                                            <>
                                                                <label>Альтернативний Wi-Fi SSID
                                                                    <input
                                                                        type="text"
                                                                        value={editedData.wifi_ssid_alt || ""}
                                                                        placeholder="Опційно / порожньо = очистити"
                                                                        onChange={(e) => setEditedData((prev) => ({
                                                                            ...prev,
                                                                            wifi_ssid_alt: e.target.value,
                                                                            wifi_alt_clear: !String(e.target.value || "").trim(),
                                                                        }))}
                                                                    />
                                                                </label>
                                                                <label>Пароль альтернативного Wi-Fi
                                                                    <input
                                                                        type="password"
                                                                        value={editedData.wifi_pass_alt || ""}
                                                                        placeholder="Новий пароль або залиште порожнім"
                                                                        disabled={!String(editedData.wifi_ssid_alt || "").trim()}
                                                                        onChange={(e) => setEditedData((prev) => ({ ...prev, wifi_pass_alt: e.target.value }))}
                                                                    />
                                                                </label>
                                                            </>
                                                        ) : null}
                                                        {(resolveSensorChannel(sensor) === "mq2" || resolveSensorChannel(sensor) === "both") ? (
                                                            <>
                                                                <label>Дим попередження<input type="number" min={0} max={4095} value={editedData.thresholds?.mq2_warning ?? DEFAULT_SENSOR_THRESHOLDS.mq2_warning} onChange={(e) => setEditedData((prev) => ({ ...prev, thresholds: { ...prev.thresholds, mq2_warning: e.target.value } }))} /></label>
                                                                <label>Дим тривога<input type="number" min={0} max={4095} value={editedData.thresholds?.mq2_alarm ?? DEFAULT_SENSOR_THRESHOLDS.mq2_alarm} onChange={(e) => setEditedData((prev) => ({ ...prev, thresholds: { ...prev.thresholds, mq2_alarm: e.target.value } }))} /></label>
                                                            </>
                                                        ) : null}
                                                        {(resolveSensorChannel(sensor) === "mq135" || resolveSensorChannel(sensor) === "both") ? (
                                                            <>
                                                                <label>Повітря попередження<input type="number" min={0} max={4095} value={editedData.thresholds?.mq135_warning ?? DEFAULT_SENSOR_THRESHOLDS.mq135_warning} onChange={(e) => setEditedData((prev) => ({ ...prev, thresholds: { ...prev.thresholds, mq135_warning: e.target.value } }))} /></label>
                                                                <label>Повітря тривога<input type="number" min={0} max={4095} value={editedData.thresholds?.mq135_alarm ?? DEFAULT_SENSOR_THRESHOLDS.mq135_alarm} onChange={(e) => setEditedData((prev) => ({ ...prev, thresholds: { ...prev.thresholds, mq135_alarm: e.target.value } }))} /></label>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </>
                                            ) : renderFlagPreview(sensor)}
                                        </td>
                                        <td>{formatDateTime(sensor.last_seen_at, timezoneFromSensorConfig(sensor.config))}</td>
                                        <td>
                                            <div>{formatClimateMetricsLine(sensor) || "—"}</div>
                                            {formatBatteryLine(sensor) ? <div>{formatBatteryLine(sensor)}</div> : null}
                                        </td>
                                        <td>{statusLabelMap[String(sensor.last_status || "info").toLowerCase()] || sensor.last_status || "Норма"}</td>
                                        <td className="actions" onClick={(e) => e.stopPropagation()}>
                                            {renderBleSetupButton(sensor)}
                                            {isEditing ? (
                                                <>
                                                    <button className="icon-button" onClick={() => handleSave(sensor.id)} title="Зберегти" disabled={saving}>
                                                        <Save className="icon-btn" />
                                                    </button>
                                                    <button className="icon-button" onClick={handleCancelEdit} title="Скасувати" disabled={saving}>
                                                        <X className="icon-btn" />
                                                    </button>
                                                </>
                                            ) : (
                                                <button className="icon-button" onClick={() => startEdit(sensor)} title="Редагувати" disabled={saving}>
                                                    <Edit className="icon-btn" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <section className="sensor-panel operator-sensors-history-panel">
                <div className="sensor-panel-header">
                    <h3><ShieldAlert size={18} /> Історія подій</h3>
                </div>
                {!selectedSensor ? (
                    <div className="sensor-empty-state">Оберіть сенсор, щоб побачити історію.</div>
                ) : !events.length ? (
                    <div className="sensor-empty-state">Подій для цього сенсора ще немає.</div>
                ) : isMobile ? (
                    <div className="sensor-events-list">
                        {events.map((item) => {
                            const severityKey = String(item.severity || "info").toLowerCase();
                            return (
                                <div
                                    key={item.id}
                                    className={`sensor-event-card ${severityKey} ${Number(highlightedEventId) === Number(item.id) ? "highlighted" : ""}`}
                                >
                                    <div className="sensor-event-header">
                                        <strong>{item.title || "Подія сенсора"}</strong>
                                        <span className={`sensor-status-badge ${severityKey}`}>
                                            {severityLabelMap[severityKey] || item.severity}
                                        </span>
                                    </div>
                                    <div className="sensor-meta-row">{formatDateTime(item.created_at, timezoneFromSensorConfig(selectedSensor?.config))} • {item.event_kind}</div>
                                    {item.body && <p className="sensor-event-body">{item.body}</p>}
                                    <div className="sensor-event-metrics">
                                        <span>Smoke: {item.reading_smoke ?? "-"}</span>
                                        <span>Air: {item.reading_air ?? "-"}</span>
                                        <span>Battery: {item.battery_level ?? "-"}</span>
                                        <span>RSSI: {item.signal_rssi ?? "-"}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="table-responsive operator-sensors-history-table-wrap">
                        <table className="operator-sensors-history-table">
                            <thead>
                                <tr>
                                    <th>Дата</th>
                                    <th>Подія</th>
                                    <th>Рівень</th>
                                    <th>Заголовок</th>
                                    <th>Опис</th>
                                    <th>Smoke</th>
                                    <th>Air</th>
                                    <th>Battery</th>
                                    <th>RSSI</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((item) => {
                                    const severityKey = String(item.severity || "info").toLowerCase();
                                    return (
                                        <tr key={item.id} className={Number(highlightedEventId) === Number(item.id) ? "operator-sensors-history-row-selected" : ""}>
                                            <td>{formatDateTime(item.created_at, timezoneFromSensorConfig(selectedSensor?.config))}</td>
                                            <td>{item.event_kind || "-"}</td>
                                            <td>
                                                <span className={`sensor-status-badge ${severityKey}`}>
                                                    {severityLabelMap[severityKey] || item.severity}
                                                </span>
                                            </td>
                                            <td>{item.title || "Подія сенсора"}</td>
                                            <td className="operator-sensors-history-body">{item.body || "-"}</td>
                                            <td>{item.reading_smoke ?? "-"}</td>
                                            <td>{item.reading_air ?? "-"}</td>
                                            <td>{item.battery_level ?? "-"}</td>
                                            <td>{item.signal_rssi ?? "-"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default OperatorSensors;
